import React from 'react';
import {classes, style} from './doppe-action-lock-preview.st.css';
import {
    AsyncValueStates,
    evaluateFunction,
    HtmlObjectResizeMode,
    uiPartCreateMarking,
    Values
} from '@wix/devzai-utils-common';
import {
    DoppeActionTypeWidgetContentLayoutContentAlignment
} from '../doppe-action-type-widget-content-layout-new/doppe-action-type-widget-content-layout-new';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    ButtonType,
    RichTextContent,
    richTextContentIsNullableOrWhiteSpace,
    useAsyncOperation
} from '@wix/devzai-utils-react';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapper
} from '../doppe-action-type-widget-content-layout-new-wrapper/doppe-action-type-widget-content-layout-new-wrapper';
import {useDoppeViewerLocales} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import IconLock from '../doppe-action-button-templates/doppe-standard-action-button-template/icons/Lock.svg';
import {
    DoppeActionTypeWidgetInnerButton
} from '../doppe-action-type-widget-inner-button/doppe-action-type-widget-inner-button';
import {WixMediaResource} from '@wix/devzai-common-wix';
import {DoppePageMediaView} from '../doppe-page-media-view/doppe-page-media-view';
import {DoppeHideableValue, doppeHideableValueIsVisible} from '../../client-server-common';
import {
    DoppeActionContentTipTapExtensions
} from '../../client-server-common/doppe-tiptap-extensions/doppe-tiptap-extensions';
import {DoppeActionLockPreviewParts} from "./doppe-action-lock-preview.ui-parts";

export const DoppeActionLockReason = {
    Lock: 'lock',
    SensetiveContent: 'sensetiveContent',
    AdultContent: 'adultContent',
    Warning: 'warning',
} as const;

export type DoppeActionLockReason = Values<typeof DoppeActionLockReason>;

export const DoppeActionLockPreview = React.memo(function DoppeActionLockPreview(props: DoppeActionLockPreview.Props) {
    const {
        media = null,
        heading,
        description,
        buttonText,
        className,
        reason,
        unlockCallback,
        useDefaultContent,
        ...htmlAttributes
    } = props;

    const doppeViewerLocales = useDoppeViewerLocales();

    const [unlockOperation, setUnlcokOperation] = useAsyncOperation();

    const widgetContent = evaluateFunction(() => {

        const isDescriptionEmpty = doppeHideableValueIsVisible(description) ? richTextContentIsNullableOrWhiteSpace(description, DoppeActionContentTipTapExtensions) : false;

        switch (reason) {
            case DoppeActionLockReason.Lock: {
                return {
                    title: useDefaultContent || heading === '' ? doppeViewerLocales['contentLockedTitleDefault'] : heading,
                    description: useDefaultContent || isDescriptionEmpty ? doppeViewerLocales['contentLockedDescriptionDefault'] : description,
                    button: useDefaultContent || buttonText === '' ? doppeViewerLocales['contentLockedButtonDefault'] : buttonText,
                    icon: IconLock
                }
            }
            case DoppeActionLockReason.SensetiveContent: {
                return {
                    title: useDefaultContent || heading === '' ? doppeViewerLocales['contentSensitiveTitleDefault'] : heading,
                    description: useDefaultContent || isDescriptionEmpty ? doppeViewerLocales['contentSensitiveDescriptionDefault'] : description,
                    button: useDefaultContent || buttonText === '' ? doppeViewerLocales['contentSensitiveButtonDefault'] : buttonText,
                    icon: IconLock
                }
            }
            case DoppeActionLockReason.AdultContent: {
                return {
                    title: useDefaultContent || heading === '' ? doppeViewerLocales['contentAdultTitleDefault'] : heading,
                    description: useDefaultContent || isDescriptionEmpty ? doppeViewerLocales['contentAdultDescriptionDefault'] : description,
                    button: useDefaultContent || buttonText === '' ? doppeViewerLocales['contentAdultButtonDefault'] : buttonText,
                    icon: IconLock
                }
            }
            case DoppeActionLockReason.Warning: {
                return {
                    title: heading,
                    description: description,
                    button: buttonText,
                    icon: IconLock
                }
            }
        }
    })

    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapper
            {...htmlAttributes}
            className={style(classes.root, className)}
            contentAlignment={DoppeActionTypeWidgetContentLayoutContentAlignment.Default}
            fullWidthMainMedia={false}
            mainMedia={!useDefaultContent && media ? (
                <DoppePageMediaView
                    className={style(classes.media, {})}
                    mediaResource={media}
                    layoutSpec={{autoHeight: true, maxHeight: 360}}
                    widthSpec={{contentRelativeWidth: 1}}
                    mediaResizeMode={HtmlObjectResizeMode.Cover}
                />
            ) : null}
        >
            <DoppeActionTypeWidgetInnerTitle
                className={style(classes.title, {})}
            >
                {widgetContent.title}
            </DoppeActionTypeWidgetInnerTitle>
            <DoppeActionTypeWidgetInnerDescription
                className={style(classes.description, {})}
            >
                {widgetContent.description}
            </DoppeActionTypeWidgetInnerDescription>
            <DoppeActionTypeWidgetInnerButton
                className={style(classes.button, {})}
                buttonType={ButtonType.NormalButton}
                onClick={async () => {
                    await setUnlcokOperation(evaluateFunction(async () => {
                        await unlockCallback();
                    }));
                }}
                suffixIcon={widgetContent.icon}
                isLoading={unlockOperation?.state === AsyncValueStates.Pending}
                {...uiPartCreateMarking(DoppeActionLockPreviewParts.UnlockButton)}
            >
                {widgetContent.button}
            </DoppeActionTypeWidgetInnerButton>
        </DoppeActionTypeWidgetContentLayoutNewWrapper>
    );
});

export namespace DoppeActionLockPreview {

    export interface Props extends React.HTMLAttributes<any> {
        media: WixMediaResource | null;
        heading: DoppeHideableValue<string>;
        description: DoppeHideableValue<RichTextContent>;
        buttonText: string;
        reason: DoppeActionLockReason;
        useDefaultContent: boolean;
        unlockCallback: () => Promise<void>;
    }
}
